import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useMotionTemplate, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown, Layers, Wand2, ShieldCheck, Heart } from "lucide-react";
import { Particles } from "@/components/Particles";
import { HeroCarousel } from "@/components/HeroCarousel";
import { MetalPanel } from "@/components/MetalPanel";
import { ARTWORKS, CATEGORIES } from "@/data/artworks";
import { Seo } from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];

const wordReveal = (delay) => ({
  initial: { y: "110%" },
  animate: { y: 0 },
  transition: { duration: 1, delay, ease: EASE },
});

const Hero = () => {
  const lx = useMotionValue(-500);
  const ly = useMotionValue(-500);
  const light = useMotionTemplate`radial-gradient(650px at ${lx}px ${ly}px, rgba(0,229,255,0.06), transparent 70%)`;

  const scrollToCollection = () => {
    const el = document.getElementById("collection");
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="home-hero"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-12"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        lx.set(e.clientX - r.left);
        ly.set(e.clientY - r.top);
      }}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_-10%,rgba(120,120,135,0.12),transparent)]" />
      <motion.div className="absolute inset-0 pointer-events-none" style={{ background: light }} />
      <Particles />

      <div className="relative z-10 text-center px-6">
        <div className="overflow-hidden mb-5">
          <motion.p {...wordReveal(0.2)} className="font-tech text-[10px] md:text-xs uppercase tracking-[0.45em] text-cyan-300/80">
            Premium Metal Art · Regina, Saskatchewan
          </motion.p>
        </div>
        <h1 className="font-tech font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
          <span className="block overflow-hidden">
            <motion.span {...wordReveal(0.35)} className="block text-silver-gradient">Art.</motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span {...wordReveal(0.5)} className="block text-silver-gradient">Made to Last.</motion.span>
          </span>
        </h1>
        <div className="overflow-hidden mt-5 max-w-xl mx-auto">
          <motion.p {...wordReveal(0.7)} className="text-sm md:text-base text-[#A1A1AA] leading-relaxed">
            Turn your favorite moments, characters, and artwork into premium metal art.
          </motion.p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.9 }}
        className="relative z-10 mt-4 md:mt-6"
      >
        <HeroCarousel />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
        className="relative z-10 mt-4 flex flex-col sm:flex-row gap-4 justify-center px-6"
      >
        <button
          onClick={scrollToCollection}
          data-testid="hero-explore-cta"
          className="inline-flex items-center justify-center gap-3 bg-white text-black font-tech text-[11px] font-semibold uppercase tracking-[0.25em] px-9 py-4 rounded-full hover:bg-cyan-200 transition-[background-color,box-shadow,transform] duration-300 hover:shadow-[0_0_32px_rgba(0,229,255,0.35)] hover:-translate-y-0.5"
        >
          Explore MetalMuse <ArrowDown size={14} />
        </button>
        <Link
          to="/order"
          data-testid="hero-create-cta"
          className="inline-flex items-center justify-center gap-3 border border-white/20 text-white font-tech text-[11px] font-semibold uppercase tracking-[0.25em] px-9 py-4 rounded-full hover:border-cyan-400/50 hover:text-cyan-200 transition-colors duration-300 backdrop-blur-md"
        >
          Create Your Own <ArrowRight size={14} />
        </Link>
      </motion.div>
    </section>
  );
};

const Experience = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotate = useTransform(scrollYProgress, [0, 1], [28, -28]);
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section ref={ref} data-testid="home-experience" className="relative py-28 md:py-40 overflow-hidden border-t border-white/[0.04]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_50%_50%,rgba(0,229,255,0.04),transparent)]" />
      <div className="container-lux relative z-10">
        <div className="text-center mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="font-tech font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white/40"
          >
            Not just a print.
          </motion.h2>
          <motion.h2
            initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
            className="font-tech font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-silver-gradient mt-2"
          >
            A piece of art.
          </motion.h2>
        </div>

        <div className="grid grid-cols-3 gap-6 md:gap-12 max-w-4xl mx-auto items-center" style={{ perspective: 1200 }}>
          <motion.div style={{ y: reduced ? 0 : y1, rotateY: reduced ? 0 : rotate }} className="will-change-transform">
            <MetalPanel src={ARTWORKS[4].src} alt="Neon Muse metal panel" testId="experience-panel-1" />
          </motion.div>
          <motion.div style={{ rotateY: reduced ? 0 : rotate }} className="scale-110 z-10 will-change-transform">
            <MetalPanel src={ARTWORKS[6].src} alt="Blue Eyes metal panel" testId="experience-panel-2" />
          </motion.div>
          <motion.div style={{ y: reduced ? 0 : y2, rotateY: reduced ? 0 : rotate }} className="will-change-transform">
            <MetalPanel src={ARTWORKS[7].src} alt="Night Watch metal panel" testId="experience-panel-3" />
          </motion.div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.06] mt-24 max-w-4xl mx-auto">
          {[
            ["Sublimated surface", "Ink infused into the metal — not printed on top."],
            ["Machined edges", "Clean metallic sides with real physical depth."],
            ["High-definition detail", "Sharper than paper, canvas or acrylic."],
            ["Gallery reflections", "A subtle metallic luster that shifts with light."],
          ].map(([t, d], i) => (
            <motion.div
              key={t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
              className="bg-[#070708] p-6 md:p-8"
              data-testid={`experience-detail-${i}`}
            >
              <p className="font-tech text-[10px] uppercase tracking-[0.25em] text-cyan-300/80">{t}</p>
              <p className="text-xs text-[#A1A1AA] mt-3 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Collection = () => (
  <section id="collection" data-testid="home-collection" className="py-28 md:py-40 border-t border-white/[0.04]">
    <div className="container-lux">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mb-16"
      >
        <p className="font-tech text-[10px] uppercase tracking-[0.45em] text-cyan-300/80 mb-4">The Collection</p>
        <h2 className="font-tech font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
          Choose your world
        </h2>
      </motion.div>

      <div className="flex flex-col md:flex-row gap-3 md:h-[520px]">
        {CATEGORIES.map((c, i) => (
          <motion.div
            key={c.name}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
            className="group relative flex-1 md:hover:flex-[2.2] transition-[flex] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] overflow-hidden h-64 md:h-auto"
            data-testid={`collection-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Link to={c.to} data-cursor="panel" className="absolute inset-0 block">
              <img
                src={c.src}
                alt={`${c.name} metal art category`}
                loading="lazy"
                className="w-full h-full object-cover transition-[transform,filter] duration-700 grayscale-[0.4] group-hover:grayscale-0 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
              <div className="absolute inset-0 border border-white/[0.08] group-hover:border-cyan-400/30 transition-colors duration-500" />
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <p className="font-tech font-bold uppercase tracking-[0.15em] text-lg md:text-xl text-white">{c.name}</p>
                <p className="text-xs text-white/60 mt-2 max-w-[220px] md:opacity-0 md:group-hover:opacity-100 md:translate-y-2 md:group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                  {c.desc}
                </p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const CustomArt = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.45"] });
  const clip = useTransform(scrollYProgress, [0, 1], ["inset(0 100% 0 0)", "inset(0 0% 0 0)"]);

  return (
    <section ref={ref} data-testid="home-custom-art" className="py-28 md:py-40 border-t border-white/[0.04] bg-[#070708]">
      <div className="container-lux grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="font-tech text-[10px] uppercase tracking-[0.45em] text-cyan-300/80 mb-4">Custom Art</p>
          <h2 className="font-tech font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            Your image<span className="text-cyan-300 mx-3">→</span>MetalMuse
          </h2>
          <p className="text-sm md:text-base text-[#A1A1AA] mt-6 leading-relaxed max-w-md">
            Send us any photo or artwork — a memory, a pet, a character, a design. We enhance it, sublimate it into aluminum, and hand you a piece built for your wall.
          </p>
          <Link
            to="/order"
            data-testid="custom-art-cta"
            className="mt-10 inline-flex items-center gap-3 bg-white text-black font-tech text-[11px] font-semibold uppercase tracking-[0.25em] px-9 py-4 rounded-full hover:bg-cyan-200 transition-[background-color,box-shadow,transform] duration-300 hover:shadow-[0_0_32px_rgba(0,229,255,0.35)] hover:-translate-y-0.5"
          >
            Turn Your Image Into Art <ArrowRight size={14} />
          </Link>
        </div>

        <div className="relative max-w-sm mx-auto w-full" style={{ perspective: 1000 }}>
          <div className="relative aspect-[4/5]">
            <div className="absolute inset-0 rounded-lg overflow-hidden opacity-80">
              <img
                src={ARTWORKS[6].src}
                alt="Original photo before transformation"
                loading="lazy"
                className="w-full h-full object-cover grayscale contrast-75 brightness-75"
              />
              <div className="absolute top-4 left-4 text-[9px] font-tech uppercase tracking-[0.3em] text-white/50 bg-black/50 px-3 py-1.5 rounded-full">
                Your Image
              </div>
            </div>
            <motion.div style={{ clipPath: reduced ? "none" : clip }} className="absolute inset-0 will-change-[clip-path]">
              <MetalPanel src={ARTWORKS[6].src} alt="Transformed MetalMuse panel" testId="custom-art-panel" />
              <div className="absolute top-4 left-4 text-[9px] font-tech uppercase tracking-[0.3em] text-cyan-200 bg-black/60 px-3 py-1.5 rounded-full border border-cyan-400/30">
                MetalMuse
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const WHY = [
  { icon: Layers, title: "Premium Metal", text: "Crisp artwork printed on metal panels." },
  { icon: Wand2, title: "Custom Art", text: "Turn your favorite image into something physical." },
  { icon: ShieldCheck, title: "Built to Last", text: "A piece designed to stay on your wall for years." },
  { icon: Heart, title: "Made for You", text: "Every piece starts with something meaningful." },
];

const Why = () => (
  <section data-testid="home-why" className="py-28 md:py-40 border-t border-white/[0.04]">
    <div className="container-lux">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mb-16"
      >
        <p className="font-tech text-[10px] uppercase tracking-[0.45em] text-cyan-300/80 mb-4">Why MetalMuse</p>
        <h2 className="font-tech font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
          Built different
        </h2>
      </motion.div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {WHY.map((b, i) => (
          <motion.div
            key={b.title}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: i * 0.12, ease: EASE }}
            data-testid={`why-card-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
            className="group border border-white/[0.06] bg-[#0A0A0B] p-8 md:p-10 hover:border-cyan-400/30 transition-colors duration-500"
          >
            <span className="font-tech text-xs text-white/25">0{i + 1}</span>
            <b.icon size={22} className="text-cyan-300/80 mt-6 mb-5 group-hover:scale-110 transition-transform duration-300" />
            <h3 className="font-tech text-sm font-semibold uppercase tracking-[0.15em] text-white">{b.title}</h3>
            <p className="text-xs text-[#A1A1AA] mt-3 leading-relaxed">{b.text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Featured = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const rotateY = useTransform(scrollYProgress, [0, 1], [-26, 14]);
  const scale = useTransform(scrollYProgress, [0, 0.6, 1], [0.78, 1, 1.06]);
  const lightX = useTransform(scrollYProgress, [0, 1], ["20%", "80%"]);
  const captionOpacity = useTransform(scrollYProgress, [0.55, 0.8], [0, 1]);
  const captionY = useTransform(scrollYProgress, [0.55, 0.8], [30, 0]);
  const lightBg = useMotionTemplate`radial-gradient(700px at ${lightX} 40%, rgba(0,229,255,0.06), transparent 70%)`;

  return (
    <section ref={ref} data-testid="home-featured" className="relative h-[260vh] border-t border-white/[0.04] bg-[#070708]">
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center overflow-hidden">
        <motion.div className="absolute inset-0 pointer-events-none" style={{ background: lightBg }} />
        <p className="font-tech text-[10px] uppercase tracking-[0.45em] text-cyan-300/80 mb-10">Featured Artwork</p>
        <div style={{ perspective: 1400 }}>
          <motion.div
            style={{ rotateY: reduced ? 0 : rotateY, scale: reduced ? 1 : scale, transformStyle: "preserve-3d" }}
            className="w-[62vw] max-w-[300px] md:max-w-[360px] will-change-transform"
          >
            <MetalPanel src={ARTWORKS[0].src} alt="Ronin in Rain — featured metal artwork" testId="featured-panel" />
          </motion.div>
        </div>
        <motion.div style={{ opacity: captionOpacity, y: captionY }} className="text-center mt-12">
          <p className="font-tech text-sm uppercase tracking-[0.25em] text-white">Ronin in Rain</p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mt-2">8×10 Sublimated Aluminum · Anime Series</p>
          <Link
            to="/shop"
            data-testid="featured-shop-cta"
            className="mt-8 inline-flex items-center gap-3 border border-white/20 text-white font-tech text-[10px] font-semibold uppercase tracking-[0.25em] px-8 py-3.5 rounded-full hover:border-cyan-400/50 hover:text-cyan-200 transition-colors duration-300"
          >
            View in Shop <ArrowRight size={13} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const FinalCta = () => (
  <section data-testid="home-final-cta" className="relative py-32 md:py-48 overflow-hidden border-t border-white/[0.04]">
    <motion.div
      animate={{ y: [0, -18, 0], rotateZ: [-4, -2, -4] }}
      transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      className="absolute right-[6%] top-1/2 -translate-y-1/2 w-40 md:w-60 opacity-25 pointer-events-none hidden sm:block"
    >
      <MetalPanel src={ARTWORKS[2].src} alt="" sheen={false} />
    </motion.div>
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_60%_at_50%_100%,rgba(0,229,255,0.05),transparent)]" />
    <div className="container-lux relative z-10">
      <motion.h2
        initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: EASE }}
        className="font-tech font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-silver-gradient max-w-3xl leading-[1.05]"
      >
        Your wall deserves more.
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
        className="text-base text-[#A1A1AA] mt-6"
      >
        Make it MetalMuse.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
        className="mt-12"
      >
        <Link
          to="/shop"
          data-testid="final-shop-cta"
          className="inline-flex items-center gap-3 bg-white text-black font-tech text-xs font-semibold uppercase tracking-[0.25em] px-12 py-5 rounded-full hover:bg-cyan-200 transition-[background-color,box-shadow,transform] duration-300 hover:shadow-[0_0_40px_rgba(0,229,255,0.4)] hover:-translate-y-1"
        >
          Shop MetalMuse <ArrowRight size={15} />
        </Link>
      </motion.div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page" className="font-outfit bg-[#050505]">
      <Seo
        title="MetalMuse — Premium Metal Photo Prints in Saskatchewan | Art. Made to Last."
        description="Turn your favourite photos, characters and artwork into premium sublimation metal prints. Waterproof, fade & scratch resistant, ready to hang. Regina, Saskatchewan — same-day delivery available."
        path="/"
        keywords="metal photo prints, sublimation printing, metal wall art, anime metal posters, Saskatchewan, custom photo prints, metal prints Canada, Regina"
      />
      <Hero />
      <Experience />
      <Collection />
      <CustomArt />
      <Why />
      <Featured />
      <FinalCta />
    </div>
  );
}
